<template>
  <div
    class="flex h-screen w-screen flex-col items-center justify-center py-20"
  >
    <h1 class="relative text-[220px] font-extrabold text-grey-lightest">
      <img
        class="absolute -z-10 h-full w-full"
        src="~/assets/svgs/error-bg.svg"
        alt="error-background"
      />
      <span class="z-10">
        {{ customError.code }}
      </span>
    </h1>

    <div class="flex max-w-[75%] flex-col items-center gap-4 text-center">
      <h2 class="text-2xl font-bold">{{ customError.type }}</h2>
      <p class="text-grey-lightest">
        {{ customError.message }}
      </p>
      <button
        v-if="customError.action"
        class="rounded-lg bg-black px-16 py-4 font-bold text-white hover:bg-black/80"
        to="/"
        @click="customError.action"
      >
        {{ customError.actionLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import useB2bUser from "~/composables/UserManagement/useB2bUser";
import paths from "./constants/paths";

type NuxtError = {
  statusCode: number;
  fatal: boolean;
  unhandled: boolean;
  statusMessage?: string;
  data?: any;
  cause?: any;
};

const { logout } = useB2bUser();

const props = defineProps({
  error: Object as () => NuxtError,
});

console.log(props.error);

const handleTryAgain = (path?: keyof typeof paths, clearErr = false) => {
  if (clearErr) clearError();
  reloadNuxtApp({
    path: path ? paths[path].toString() : undefined,
    force: true,
  });
};

const handleAuthError = () => {
  logout();
  handleTryAgain();
};

const customError = computed(() => {
  const errorCode = props.error?.statusCode?.toString() ?? "500";
  let errorType, message, actionLabel, action: () => void;

  // Network Errors
  if (errorCode.startsWith("4")) {
    // Auth Error
    if (
      props.error?.data?.meta?.errors?.[0]?.type === "AuthenticationException"
    ) {
      errorType = "Authentication Exception";
      actionLabel = "Logout and Try Again";
      message =
        "There was a problem with credentials. Please logout and try again.";
      action = handleAuthError;
    }
    //Generic Network Error
    else {
      errorType = "Network Error";
      actionLabel = "Try Again";
      message =
        "There was a problem with your request. Please check your network connection.";
      action = () => handleTryAgain("home", true);
    }
  }
  // Server Errors
  else if (errorCode.startsWith("5")) {
    errorType = "Internal Server Error";
    actionLabel = "Try Again";
    message = "Oops! Something went wrong on our end. Please try again later.";
    action = () => handleTryAgain("home", true);
  }
  // Default to generic error
  else {
    errorType = "Unknown Error";
    actionLabel = "Try Again";
    message = "An unexpected error occurred.";
    action = () => handleTryAgain(undefined, true);
  }

  return {
    code: errorCode,
    type: errorType,
    message,
    actionLabel,
    action,
  };
});
</script>
